import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  Hidden,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { GetServerSidePropsContext } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import {
  getAgencyIdFromRequest,
  getLanguageFromRequest,
  getTranslation,
  getTranslationFromArray,
} from "../utils";
import sdk from "../sdk";
import { AttributeExtra, PropertyProfile } from "@serviestate/shared";
import { useTranslation } from "next-i18next";
import { FC, useCallback, useMemo, useState } from "react";
import { BsCheck2 } from "react-icons/bs";
import { BiBed, BiBath, BiShapePolygon, BiTransfer } from "react-icons/bi";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Circle,
  Polygon,
} from "@react-google-maps/api";
import { ContactUsInformation } from "../components/contact-us";
import { CertificateEnergy } from "../components/certificate-energy";
import { useForm } from "react-hook-form";
import dynamic from "next/dynamic";
const Lottie = dynamic(() => import("lottie-react"), { ssr: false });
import sendedOk from "../lotties/91068-message-sent-successfully-plane.json";
import { track } from "../tracking";
import { Gallery } from "../components/gallery";
import { isValidPhoneNumber } from "libphonenumber-js";
import Trans from "../components/trans";
import Head from "next/head";
import MortgageCalculator from "../components/mortgage-calculator";

export async function getServerSideProps({
  query,
  req,
  resolvedUrl,
}: GetServerSidePropsContext) {
  const locale = getLanguageFromRequest(req);
  const reference = query.reference as string;

  if (!reference) {
    return {
      redirect: {
        permanent: false,
        destination: "/",
      },
    };
  }

  const agencyId = getAgencyIdFromRequest(req);

  const [property, extrasAvailables, locales] = await Promise.all([
    sdk.website.getProperty({
      agency_id: agencyId,
      reference: decodeURIComponent(reference),
    }),
    sdk.attributes.getExtras(),
    serverSideTranslations(locale, ["common"]),
  ]);

  track(req, resolvedUrl, "property_sheet", { property_id: property.id });

  const propertyAttributesAvailables = Object.keys(property.attributes).filter(
    (x) =>
      property.attributes[x] !== "" &&
      property.attributes[x] !== "0" &&
      property.attributes[x] !== false &&
      property.attributes[x] !== 0 &&
      property.attributes[x] !== null &&
      property.attributes[x] !== undefined
  );

  const propertyExtras = extrasAvailables.filter((x) =>
    x.related_attributes.some((a) => propertyAttributesAvailables.includes(a))
  );

  return {
    props: {
      ...locales,
      property,
      propertyExtras,
    },
  };
}

export default function PropertySheet({
  property,
  propertyExtras,
}: {
  property: PropertyProfile;
  propertyExtras: AttributeExtra[];
}) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const translation = getTranslation(language, property.translations);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyAsI0DsoNgKAIVoKc_OqVBpicyyg6hLIHA",
  });

  const [mapLoaded, setMapLoaded] = useState(false);

  const zoom = property.address ? 18 : 13;

  const propertyPhotos = property.multimedia.map((x, i) => ({
    resource: x.document_id,
    title: `photo ${i}`,
  }));

  const lastPhotoResource = propertyPhotos?.pop()?.resource;
  const propertyThumbnail = lastPhotoResource
    ? lastPhotoResource.toString()
    : undefined;

  const coords = useMemo(
    () =>
      property.address || (!property.zone.latitude && !property.zone.longitude)
        ? { lat: property.latitude, lng: property.longitude }
        : { lat: property.zone.latitude, lng: property.zone.longitude },
    [property]
  );

  const htmlDescription =
    translation?.description?.replace(/\n/gm, "<br>") ?? "";

  const details = useMemo(() => {
    const data: string[] = [];
    data.push(`${t("filter.reference")}: ${property.reference}`);
    if (property.transfer_price) {
      data.push(
        `${t("details.transfer")}: ${property.transfer_price.toLocaleString(
          "de-DE"
        )}€`
      );
    }
    [
      "useful_surface",
      "builded_surface",
      "buildable_surface",
      "plot_surface",
      "terrace_surface",
      "garden_surface",
      "kitchen_surface",
      "diningroom_surface",
      "livingroom_surface",
    ].forEach(
      (x) =>
        property.attributes[x] &&
        data.push(
          `${t(`details.${x}`)}: ${parseFloat(
            property.attributes[x] + ""
          ).toLocaleString("de-DE")}m<small><sup>2</sup></small>`
        )
    );
    if (property.attributes.construction_year)
      data.push(
        `${t("details.construction_year")}: ${
          property.attributes.construction_year
        }`
      );
    if (property.attributes.reformation_year)
      data.push(
        `${t("details.reformation_year")}: ${
          property.attributes.reformation_year
        }`
      );
    if (property.attributes.double_bedrooms)
      data.push(
        `${t("details.double_bedrooms")}: ${
          property.attributes.double_bedrooms
        }`
      );
    if (property.attributes.individual_bedrooms)
      data.push(
        `${t("details.individual_bedrooms")}: ${
          property.attributes.individual_bedrooms
        }`
      );
    if (property.attributes.bathrooms)
      data.push(`${t("details.bathrooms")}: ${property.attributes.bathrooms}`);
    if (property.attributes.washrooms)
      data.push(`${t("details.washrooms")}: ${property.attributes.washrooms}`);
    return data;
  }, [property, t]);

  const extras = useMemo(() => {
    const output: string[] = [];
    for (const extra of propertyExtras) {
      const translation = getTranslationFromArray(language, extra.translations);
      output.push(translation?.name ?? extra.name);
    }
    return output;
  }, [propertyExtras, language]);

  const sellTransaction =
    property.transactions?.find((x) => x.type.id === 2) ?? null;

  const ccaa =
    (property.zone.level.level === 1
      ? property.zone
      : property.zone.components.find((x) => x.level.level === 1)) ?? null;

  const iconSize = 30;

  return (
    <>
      <Head>
        <title>{translation?.title}</title>
        {translation && translation.title && (
          <meta
            name="description"
            content={
              htmlDescription
                .substring(0, 300)
                .replaceAll(/\n/gm, " ")
                .replaceAll("<br>", " ") + "..."
            }
          />
        )}
        <meta
          property="og:image"
          content={"url(/api/documents/" + propertyThumbnail + "?format=webp)"}
        />
      </Head>
      <Container sx={{ mt: 4 }}>
        <Box
          sx={{
            display: "flex",
            mb: 3,
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Box flex={{ xs: 0, sm: 1 }}>
            <Typography variant="h1">
              {translation?.title} {t("title_junction_location")}{" "}
              {property.zone?.name}
            </Typography>
            {property.address && <Typography>{property.address}</Typography>}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 3, mt: 0.5 }}
            >
              {(property.attributes.bedrooms as number) > 0 && (
                <Typography
                  fontSize="1.4rem"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <BiBed size={iconSize} /> {property.attributes.bedrooms}
                </Typography>
              )}
              {parseInt((property.attributes.bathrooms ?? "0") as string, 10) >
                0 && (
                <Typography
                  fontSize="1.4rem"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <BiBath size={iconSize} /> {property.attributes.bathrooms}
                </Typography>
              )}
              {(property.attributes.surface as number) > 0 && (
                <Typography
                  fontSize="1.4rem"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <BiShapePolygon size={iconSize} />{" "}
                  <span>
                    {property.attributes.surface}
                    <small>
                      m<sup>2</sup>
                    </small>
                  </span>
                </Typography>
              )}
              {property.transfer_price && (
                <Typography
                  fontSize="1.4rem"
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <BiTransfer size={iconSize} />{" "}
                  <span>
                    {property.transfer_price.toLocaleString("de-DE")}€
                  </span>
                </Typography>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.2,
              mt: { xs: 2, md: 0 },
            }}
          >
            {property.transactions.map((x) => (
              <Box key={`trx_${x.type.id}`}>
                <Typography
                  variant="h5"
                  textAlign={{ xs: "center", sm: "right" }}
                  sx={{ fontSize: "2rem" }}
                >
                  {x.price
                    ? `${x.price.toLocaleString("de-DE")}€`
                    : t("price_to_consult")}
                  {x.periodicity &&
                    ` / ${t(`periodicity.${x.periodicity.toLowerCase()}`)}`}
                </Typography>
              </Box>
            ))}
            <Box textAlign={{ xs: "center", sm: "right" }}>
              <Typography
                fontSize="1.4rem"
                sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
              >
                {property.reference}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Gallery
          photos={property.multimedia.map((x, i) => ({
            resource: x.document_id,
            title: `photo ${i}`,
            renderType: x.render_type,
          }))}
        />
        <Box
          sx={{
            mt: 3,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
          }}
        >
          <Box
            sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Paper sx={{ padding: 3 }}>
              <Typography fontWeight="700" fontSize="1.2rem" gutterBottom>
                <Trans i18nKey="property_details.description" />
              </Typography>
              <Typography
                dangerouslySetInnerHTML={{ __html: htmlDescription }}
              ></Typography>
            </Paper>
            <Paper sx={{ padding: 3 }}>
              <Typography fontWeight="700" fontSize="1.2rem" gutterBottom>
                <Trans i18nKey="property_details.details" />
              </Typography>
              <Grid container spacing={0.5}>
                {details &&
                  details.map((x, i) => (
                    <Grid key={`detail_${i}`} item xs={12} md={6}>
                      <Typography dangerouslySetInnerHTML={{ __html: x }} />
                    </Grid>
                  ))}
              </Grid>
            </Paper>
            {extras && extras.length > 0 && (
              <Paper sx={{ padding: 3 }}>
                <Typography fontWeight="700" fontSize="1.2rem" gutterBottom>
                  <Trans i18nKey="property_details.other_features" />
                </Typography>
                <Grid container spacing={0.5}>
                  {extras &&
                    extras.map((x, i) => (
                      <Grid key={`detail_${i}`} item xs={12} md={6}>
                        <Typography
                          sx={{ display: "flex", gap: 1, alignItems: "center" }}
                        >
                          <BsCheck2 size={22} color="green" /> {x}
                        </Typography>
                      </Grid>
                    ))}
                </Grid>
              </Paper>
            )}
            {(property.attributes.energy_certificate ||
              property.attributes.energy_emission_certificate) && (
              <Paper sx={{ padding: 3 }}>
                <Typography fontWeight="700" fontSize="1.2rem" flex={1}>
                  <Trans i18nKey="property_details.energy_certificate" />
                </Typography>
                <CertificateEnergy
                  energyCertificate={
                    (property.attributes.energy_certificate as string) ?? null
                  }
                  energyConsumption={
                    (property.attributes.energy_consumption as number) ?? null
                  }
                  energyEmissionCertificate={
                    (property.attributes
                      .energy_emission_certificate as string) ?? null
                  }
                  energyEmission={
                    (property.attributes.energy_emission as number) ?? null
                  }
                />
              </Paper>
            )}
            <Paper sx={{ padding: 3 }}>
              <Box
                sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}
              >
                <Typography fontWeight="700" fontSize="1.2rem" flex={1}>
                  <Trans i18nKey="property_details.location" />
                </Typography>
                <Typography gutterBottom>
                  {property.address
                    ? property.address
                    : `${t("property_details.zone")} ${
                        property.zone?.computed_name
                      }`}
                </Typography>
              </Box>
              {isLoaded && (
                <Box>
                  <GoogleMap
                    mapContainerStyle={containerStyle}
                    zoom={zoom}
                    center={coords}
                    onTilesLoaded={() => setMapLoaded(true)}
                  >
                    {mapLoaded && property.address && (
                      <Marker position={coords} />
                    )}

                    {mapLoaded &&
                      (property.address?.trim().length ?? 0) === 0 &&
                      !!property.zone &&
                      !!property.zone.area &&
                      property.zone.area.type === "Polygon" && (
                        <Polygon
                          key="area"
                          paths={property.zone.area.coordinates.flatMap(
                            (x: any) =>
                              x.map((y: any) => ({
                                lat: y[1],
                                lng: y[0],
                              }))
                          )}
                        />
                      )}

                    {mapLoaded &&
                      (property.address?.length ?? 0) === 0 &&
                      (!property.zone || !property.zone.area) && (
                        <Circle center={coords} radius={1000} />
                      )}
                  </GoogleMap>
                </Box>
              )}
            </Paper>
            {sellTransaction && ccaa && (
              <MortgageCalculator
                ccaa={ccaa}
                defaultPropertyPrice={sellTransaction.price ?? 180_000}
                defaultDownPayment={(sellTransaction.price ?? 180_000) * 0.3}
              />
            )}

            <PropertyContactForm propertyReference={property.reference} />
          </Box>
          <Box
            sx={{
              width: { xs: "100%", md: 320 },
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Hidden mdDown>
              <PropertyContactForm
                compact
                propertyReference={property.reference}
              />
            </Hidden>
            <ContactUsInformation />
          </Box>
        </Box>
      </Container>
    </>
  );
}

const PropertyContactForm: FC<{
  propertyReference: string;
  compact?: boolean;
}> = ({ propertyReference, compact }) => {
  const { t } = useTranslation("common");

  const [sendStatus, setSendStatus] = useState<
    "sending" | "sended" | "error" | null
  >(null);
  const [mobile, setMobile] = useState("");

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      mobile: "",
      email: "",
      message: "",
      accept_legal_terms: false,
    },
  });

  const handleChangeMobile = (event: any) => {
    const inputValue = event.target.value;
    if (/^[0-9]*$/.test(inputValue)) {
      setMobile(inputValue);
    }
  };

  const handleSendMessage = useCallback(
    async (form: any) => {
      setSendStatus("sending");

      try {
        const response = await fetch("/api/send-message", {
          method: "POST",
          body: JSON.stringify({
            ...form,
            property_reference: propertyReference,
          }),
          headers: { "content-type": "application/json" },
        });
        setSendStatus(response.ok ? "sended" : "error");
      } catch (e) {
        setSendStatus("error");
      }
    },
    [propertyReference]
  );

  return (
    <form onSubmit={handleSubmit(handleSendMessage)} noValidate>
      <Paper sx={{ padding: 3 }}>
        <Typography fontWeight="700" fontSize="1.2rem" gutterBottom>
          <Trans i18nKey="contact.are_you_interested" />
        </Typography>
        {sendStatus === "sended" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box sx={{ maxWidth: 300 }}>
              <Lottie animationData={sendedOk} loop={false} />
            </Box>
          </Box>
        )}

        {sendStatus !== "sended" && (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={compact ? 12 : 6}>
                <TextField
                  fullWidth
                  {...register("name", {
                    required: t("validations.person_name.required"),
                  })}
                  disabled={sendStatus === "sending"}
                  required
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  label={t("contact.person_name")}
                />
              </Grid>
              <Grid item md={compact ? 12 : 6}>
                <TextField
                  {...register("mobile", {
                    required: t("validations.mobile.required"),
                    validate: (tel: string | undefined = "") => {
                      if (tel && !isValidPhoneNumber(tel.toString(), "ES")) {
                        return t("validations.mobile.format.error");
                      }
                      return true;
                    },
                  })}
                  inputProps={{
                    pattern: "[0-9]*",
                    inputMode: "numeric",
                  }}
                  disabled={sendStatus === "sending"}
                  required
                  type="tel"
                  error={!!errors.mobile}
                  helperText={errors.mobile?.message}
                  fullWidth
                  label={t("contact.mobile")}
                  value={mobile}
                  onChange={handleChangeMobile}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register("email", {
                    required: t("validations.email.required"),
                  })}
                  disabled={sendStatus === "sending"}
                  required
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  fullWidth
                  label={t("contact.email")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register("message", {
                    required: t("validations.message.required") as string,
                  })}
                  disabled={sendStatus === "sending"}
                  required
                  error={!!errors.message}
                  helperText={errors.message?.message}
                  fullWidth
                  label={t("contact.message")}
                  multiline
                  minRows={4}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  disabled={sendStatus === "sending"}
                  label={<Trans i18nKey="data_protection.check_accept_legal" />}
                  style={errors.accept_legal_terms ? { color: "red" } : {}}
                  control={
                    <Checkbox
                      size="small"
                      style={errors.accept_legal_terms ? { color: "red" } : {}}
                      {...register("accept_legal_terms", { required: true })}
                    />
                  }
                  labelPlacement="end"
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              <Button type="submit" variant="contained" sx={{ minWidth: 140 }}>
                <Trans i18nKey="contact.send" />
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </form>
  );
};

const containerStyle = {
  width: "100%",
  height: "100%",
  minHeight: "350px",
};
